<template>
  <span class="multiselect-wrapper">
    <multiselect
      :disabled="disabled"
      track-by="_id"
      :value="selectedItem"
      label="name"
      :placeholder="$t('task.assignee')"
      :options="options"
      :searchable="true"
      :multiple="multiple"
      :close-on-select="!multiple"
      :allow-empty="multiple"
      :show-labels="false"
      @input="changed"
      @search-change="search = $event"
    >
      <!-- TKP-80: çoklu seçimde seçilenler etiket olarak görünür ve tek tek çıkarılabilir -->
      <template v-if="multiple" slot="tag" slot-scope="{ option, remove }">
        <span class="badge badge-soft-primary mr-1 mb-1 d-inline-flex align-items-center">
          {{ option.name }}
          <a href="javascript:;" class="ml-1 text-danger" @click="remove(option)"><i class="bx bx-x"></i></a>
        </span>
      </template>
      <template slot="singleLabel" slot-scope="{ option }">
        <div class="d-flex">
          <letter-avatar :name="option.name" :image="option.avatar" variant="primary"> </letter-avatar>
          <span class="ml-2">
            {{ option.name }}
            <div class="text-muted">
              <span v-if="option.email"> {{ option.email }}</span>
              <span v-else-if="option.credential"> {{ option.credential.email }}</span>
            </div>
          </span>
        </div>
      </template>
      <template slot="option" slot-scope="{ option }">
        <div class="d-flex">
          <letter-avatar :name="option.name" :image="option.avatar" variant="primary"> </letter-avatar>
          <span class="ml-2">
            {{ option.name }}
            <div class="text-muted">
              <span v-if="option.email"> {{ option.email }}</span>
              <span v-else-if="option.credential"> {{ option.credential.email }}</span>
            </div>
          </span>
        </div>
      </template>
      <slot v-if="isorganizationMembersLoading" slot="noOptions"> <content-loading /></slot>
      <slot v-else slot="noOptions">{{ $t("common.not-found") }}</slot>

      <slot slot="noResult">{{ $t("common.not-found") }}</slot>
    </multiselect>
  </span>
</template>

<script>
export default {
  props: ["disabled", "selected", "permissionFilter", "organization", "ignore", "filterFunction", "multiple", "prefillAccountId"], //ignore:array of memberId
  data() {
    return { options: [], selectedItem: undefined, search: undefined, prefilled: false };
  },
  computed: {
    isorganizationMembersLoading() {
      return this.$store.getters["organizationMember/isSearchLoading"];
    },
    currentAccount() {
      return this.$store.getters["user/account"];
    },
  },
  watch: {
    selected: {
      immediate: true,
      handler(v) {
        this.selectedItem = v;
      },
    },
    search: {
      deep: true,
      handler(v) {
        this.debounce(() => this.searchChanged(v));
      },
    },
  },
  async mounted() {
    this.searchChanged("*");
    if (this.selected) {
    }
  },
  methods: {
    async searchChanged(value) {
      value = (value == "*" ? "" : value).trim();

      const members = await this.fetchMembers(value);

      this.options = members;
      this.applyPrefill();
    },

    /**
     * TKP-80 / tasarım s.11 md.2 — ekleme ekranında sistem eklemeyi yapan kullanıcıyı yönetici olarak ÖN DOLU
     * getirir; kullanıcı onu çıkarabilir ya da yanına başkalarını ekleyebilir.
     *
     * Ön dolum burada yapılıyor çünkü yazılacak değer üye ACL kaydının KENDİSİ olmalı (`admins` bu kayda ref);
     * hesap kimliğinden uydurulmuş bir nesne kaydedilemez. Üye listesi zaten bu bileşende yükleniyor.
     */
    applyPrefill() {
      if (!this.prefillAccountId || this.prefilled) return;
      const alreadySelected = Array.isArray(this.selectedItem) ? this.selectedItem.length > 0 : !!this.selectedItem;
      if (alreadySelected) return;

      const member = this.options.find((option) => String(option.accountId) === String(this.prefillAccountId));
      if (!member) return;

      this.prefilled = true;
      this.changed(this.multiple ? [member] : member);
    },

    changed(values) {
      // TKP-80: çoklu seçimde `values` bir DİZİ. Eskiden dizi de kopyalanıyordu ama sonraki satırlar tek nesne
      // varsayıyordu (`delete result.processAdminOf`, `result.accountId`) — dizi geldiğinde sessizce yanlış çalışırdı.
      const strip = (item) => {
        const copy = { ...item };
        delete copy.processAdminOf;
        delete copy.departmentAdminOf;
        return copy;
      };

      if (Array.isArray(values)) {
        const result = values.map(strip);
        this.selectedItem = result;
        this.$emit("onAclSelect", result);
        this.$emit(
          "onSelect",
          result.map((item) => ({ ...item, _id: item.accountId }))
        );
        this.$forceUpdate();
        return;
      }

      const result = values ? strip(values) : undefined;
      this.selectedItem = result; //! Probleme sebep olabilir

      if (result) {
        this.$emit("onAclSelect", result);
        this.$emit("onSelect", { ...result, _id: result.accountId });
      } else {
        this.$emit("onSelect", null);
      }

      this.$forceUpdate();
    },
    async fetchMembers(value) {
      const organization = this.organization ?? this.currentOrganization;
      const response = await this.$store.dispatch("organizationMember/paginateWithForSearch", {
        pagination: { query: { accessable: { value: true, isObject: true }, name: { value } }, options: { limit: 200 } },
        organizationId: organization?._id,
      });

      return this.prepareResponse(response);
    },
    prepareResponse(response) {
      let result =
        response?.data?.docs?.filter((i) => {
          if (i.accessable && this.permissionFilter) {
            if (i.isOwner && !(i.accountId == this.currentAccount._id)) {
              return true;
            }

            return i.roles?.find((r) => r.permissions?.some((p) => this.permissionFilter.includes(p)));
          }

          return i.accessable;
        }) ?? [];

      if (this.filterFunction) {
        result = result.filter(this.filterFunction);
      }

      if (!this.ignore) {
        return result;
      } else {
        return result.filter((i) => !this.ignore.includes(i._id));
      }
    },

    itemSelected(item) {
      this.selectedItem = item;
      delete item.processAdminOf;
      delete item.departmentAdminOf;

      if (item) {
        this.$emit("onAclSelect", item);
        this.$emit("onSelect", { ...item, _id: item.accountId });
      } else {
        this.$emit("onSelect", null);
      }
    },
  },
};
</script>

<style>
.multiselect-wrapper .multiselect__option--highlight {
  background: rgb(237, 237, 237) !important;
  color: black !important;
}
</style>
